import { useClient } from '@cortex/react-query';
import type {
  Benchmark,
  BenchmarkCatalogItem,
  BenchmarkDriverConfig,
  BenchmarkDriverKind,
  BenchmarkScenarioProjection,
  BenchmarkScorecardAuth,
  BenchmarkKit,
  MemoryAnchorSet,
  MCPConnection,
  ModelConfig,
  OrchestrationGroup,
  PersistentAgentGroup,
} from '@cortex/types';
import {
  defaultStack,
  emptyBenchmarkKit,
  parseBenchmarkKit,
  simBenchmarkSetupDocUrl,
  stackDisplayName,
} from '@cortex/types';
import { fork } from '@cortex/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState, type CSSProperties } from 'react';

import { BenchmarkResults, formatKitMetricsSummary, statusBadge } from '../-benchmark-results';
import {
  MiniStat,
  PassBar,
  SectionLabel,
  formatRunCost,
  passRateColor,
  scoreRun,
} from '../-benchmark-ui';
import { DisabledToolsSelect } from '../../../../components/benchmarks/DisabledToolsSelect';
import { ConfirmDialog } from '../../../../components/common/ConfirmDialog';
import { EmptyState } from '../../../../components/common/EmptyState';
import { PageHeader } from '../../../../components/common/PageHeader';
import { Spinner } from '../../../../components/common/Spinner';
import {
  apiErrorText,
  useScorerCredentialStatus,
} from '../../../../components/mcp-connections/ScorerTokenSection';
import { routes } from '../../../../lib/constants';
import { formatDuration, formatRelative } from '../../../../lib/format';

import { BenchmarkKitEditor } from './-kit-editor';
import {
  runAgentsLabel,
  runAgentsTitle,
  runAgentsUsed,
  runModelLabel,
  runModelTitle,
} from './-run-display';

export const Route = createFileRoute('/agents/orchestration/benchmarks/')({
  component: BenchmarksPage,
});

interface IListEnvelope<T> {
  items: T[];
  total: number;
}

interface IThreadDraft {
  id: string;
  agentDefinitionId: string;
  name: string;
  facts: Record<string, string>;
  initialNotification: string;
  /**
   * Situational anchors on the thread's mission wake (DEV-17326). No editor
   * UI yet — carried through the draft so a console save doesn't strip
   * anchors that were set via the API/SDK.
   */
  anchors?: MemoryAnchorSet;
}

const stack3: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' };
const row: CSSProperties = { display: 'flex', gap: 'var(--sp-2)', alignItems: 'center' };

function BenchmarksPage() {
  const client = useClient();
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Benchmark | null>(null);
  const [startTarget, setStartTarget] = useState<Benchmark | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Benchmark | null>(null);

  const benchmarks = useQuery({
    queryKey: ['benchmarks'],
    queryFn: () => client.http.get('v1/benchmarks').json<Benchmark[]>(),
    refetchInterval: (query) =>
      query.state.data?.some(
        (item) => item.currentRun?.status === 'PREPARING' || item.currentRun?.status === 'RUNNING',
      )
        ? 2000
        : false,
  });
  const groups = useQuery({
    queryKey: ['orchestration-groups'],
    queryFn: () => client.http.get('v1/orchestration-groups').json<OrchestrationGroup[]>(),
  });
  const connections = useQuery({
    queryKey: ['mcp-connections'],
    queryFn: () =>
      client.http
        .get('v1/mcp-connections', { searchParams: { limit: '200' } })
        .json<IListEnvelope<MCPConnection>>(),
  });

  const invalidate = () => fork(qc.invalidateQueries({ queryKey: ['benchmarks'] }));
  // Experiment-arm Start form (DEV-17197): label + armId + trials + the one-lever
  // profile overrides (model / tools / disabled tools / charter / memory). Sent as a
  // full StartBenchmarkInput so an arm is auditable, not encoded in freeform label text.
  const [startLabel, setStartLabel] = useState('');
  const [startArmId, setStartArmId] = useState('');
  const [startTrials, setStartTrials] = useState('1');
  const [startModelConfigId, setStartModelConfigId] = useState('');
  const [startInstructions, setStartInstructions] = useState('');
  const [startToolRefs, setStartToolRefs] = useState('');
  const [startDisabledTools, setStartDisabledTools] = useState<string[]>([]);
  const [startMemoryMode, setStartMemoryMode] = useState('');
  const [startMemoryNamespaceId, setStartMemoryNamespaceId] = useState('');
  const resetStartForm = () => {
    setStartLabel('');
    setStartArmId('');
    setStartTrials('1');
    setStartModelConfigId('');
    setStartInstructions('');
    setStartToolRefs('');
    setStartDisabledTools([]);
    setStartMemoryMode('');
    setStartMemoryNamespaceId('');
  };
  const pageModelConfigs = useQuery({
    queryKey: ['model-configs'],
    queryFn: () => client.http.get('v1/model-configs?limit=200').json<{ items: ModelConfig[] }>(),
  });
  const buildStartBody = (): Record<string, unknown> =>
    buildStartBenchmarkBody({
      label: startLabel,
      armId: startArmId,
      trials: startTrials,
      modelConfigId: startModelConfigId,
      instructions: startInstructions,
      toolRefs: startToolRefs,
      disabledTools: startDisabledTools,
      memoryMode: startMemoryMode,
      memoryNamespaceId: startMemoryNamespaceId,
    });
  const start = useMutation({
    // Surface the API's own message: Start refusals (e.g. the Scorer token
    // preflight) name the connection and the fix.
    mutationFn: async ({ id, body }: { id: string; body: Record<string, unknown> }) => {
      try {
        return await client.http.post(`v1/benchmarks/${id}/start`, { json: body }).json();
      } catch (err) {
        throw new Error(await apiErrorText(err));
      }
    },
    onSuccess: () => {
      setStartTarget(null);
      resetStartForm();
      invalidate();
    },
  });
  const cancel = useMutation({
    mutationFn: (id: string) => client.http.post(`v1/benchmarks/${id}/cancel`, { json: {} }).json(),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => client.http.delete(`v1/benchmarks/${id}`).text(),
    onSuccess: () => {
      setDeleteTarget(null);
      invalidate();
    },
  });

  const groupNames = new Map((groups.data ?? []).map((group) => [group.id, group.name]));
  const connectionNames = new Map(
    (connections.data?.items ?? []).map((connection) => [connection.id, connection.name]),
  );

  return (
    <div style={stack3}>
      <PageHeader
        title="Benchmarks"
        actions={
          <button
            className="btn btn--primary btn--sm"
            onClick={() => {
              setEditing(null);
              setShowForm((value) => !value);
            }}
          >
            {showForm && !editing ? 'Cancel' : 'New benchmark'}
          </button>
        }
      />

      {showForm && (
        <BenchmarkForm
          benchmark={editing}
          groups={groups.data ?? []}
          connections={connections.data?.items ?? []}
          onCancel={() => {
            setShowForm(false);
            setEditing(null);
          }}
          onSaved={() => {
            setShowForm(false);
            setEditing(null);
            invalidate();
          }}
        />
      )}

      {(start.error || cancel.error || remove.error) && (
        <div className="alert alert--danger">
          {((start.error ?? cancel.error ?? remove.error) as Error).message}
        </div>
      )}

      {benchmarks.isLoading && <Spinner />}
      {benchmarks.error && (
        <div className="alert alert--danger">
          Failed to load: {(benchmarks.error as Error).message}
        </div>
      )}
      {benchmarks.data?.length === 0 && !showForm && (
        <EmptyState
          title="No benchmarks yet"
          description="Runnable = ≥1 trial thread + (kit watches/mission and/or driver scenario). Kit-only works without a benchmark MCP tool."
        />
      )}
      {(benchmarks.data ?? []).map((benchmark) => {
        const currentRun = benchmark.currentRun;
        const active = currentRun?.status === 'PREPARING' || currentRun?.status === 'RUNNING';
        const groupName = groupNames.get(benchmark.orchestrationGroupId) ?? 'Unknown group';
        const threadCount = benchmark.threadStartupConfig.agents.reduce(
          (n, a) => n + a.threads.length,
          0,
        );
        const hasKit =
          (benchmark.kit?.watches?.length ?? 0) > 0 || Boolean(benchmark.kit?.scorecard);
        const hasScenario = Boolean(benchmark.remoteBenchmarkKey?.trim());
        // Driver scenario OR kit (watches/metric). Threads always required.
        const runnable = threadCount > 0 && (hasScenario || hasKit);
        const score = currentRun ? scoreRun(currentRun) : null;
        return (
          <section className="card" key={benchmark.id}>
            <div className="card-header is-row">
              <div style={{ minWidth: 0 }}>
                <h3 className="t-h3" style={{ margin: 0 }}>
                  <Link
                    to={routes.orchestrationBenchmark(benchmark.id)}
                    style={{ color: 'inherit', textDecoration: 'none' }}
                  >
                    {benchmark.name}
                  </Link>
                </h3>
                <div className="t-sm t-secondary" style={{ marginTop: 2 }}>
                  {(() => {
                    const mcpName = connectionNames.get(benchmark.mcpConnectionId) ?? 'Unknown MCP';
                    return mcpName === groupName ? groupName : `${groupName} · ${mcpName}`;
                  })()}
                  {benchmark.remoteBenchmarkKey?.trim() ? (
                    <>
                      {' · '}
                      <code>{benchmark.remoteBenchmarkKey}</code>
                    </>
                  ) : null}
                </div>
              </div>
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--sp-2)',
                  flex: 'none',
                }}
              >
                {benchmark.driver?.kind === 'sim_scenario' && hasScenario ? (
                  <span className="badge badge--neutral">sim</span>
                ) : null}
                {hasKit ? (
                  <span
                    className="badge badge--neutral"
                    title={`${benchmark.kit?.watches?.length ?? 0} watch(es)${
                      benchmark.kit?.scorecard ? ' + scorecard' : ''
                    }`}
                  >
                    kit
                  </span>
                ) : null}
                {!runnable && !active ? <span className="badge badge--warning">draft</span> : null}
                {currentRun ? (
                  <span
                    className="badge badge--secondary"
                    title={`Ran on ${stackDisplayName(currentRun.stack)}`}
                  >
                    {stackDisplayName(currentRun.stack)}
                  </span>
                ) : null}
                {currentRun?.stack && currentRun.stack !== defaultStack ? (
                  <span
                    className="badge badge--outline"
                    title="Imported from an external harness; score computed server-side, but the run log is self-reported"
                  >
                    self-reported
                  </span>
                ) : null}
                <span className={statusBadge(currentRun?.status)}>
                  {(currentRun?.status ?? 'idle').toLowerCase()}
                </span>
              </span>
            </div>
            <div className="card-body" style={stack3}>
              {benchmark.description && (
                <p className="t-sm t-secondary" style={{ margin: 0 }}>
                  {benchmark.description}
                </p>
              )}
              {!runnable && !active && (
                <p className="t-sm t-muted" style={{ margin: 0 }}>
                  Not runnable yet — needs{' '}
                  {threadCount === 0 ? '≥1 trial thread (Edit → Add thread)' : null}
                  {threadCount === 0 && !hasKit && !hasScenario ? ' and ' : null}
                  {!hasKit && !hasScenario
                    ? 'a kit (watches / scorecard) or a driver scenario'
                    : null}
                  .
                </p>
              )}
              {active && (
                <div className="alert alert--info">
                  {currentRun?.status === 'PREPARING'
                    ? 'Clearing this group’s old threads and launching fresh named threads…'
                    : 'Agents are running. Waiting for the benchmark-driving MCP to complete…'}
                </div>
              )}
              {currentRun?.status === 'FAILED' && (
                <div className="alert alert--danger">
                  <strong>Last run failed</strong>
                  {currentRun.errorMessage ? `: ${currentRun.errorMessage}` : '.'} Check API logs /
                  MCP <code>benchmark</code> tool, fix config, Start again.
                </div>
              )}
              {currentRun && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--sp-2)',
                      flexWrap: 'wrap',
                    }}
                  >
                    <SectionLabel>Latest run</SectionLabel>
                    <span className="t-sm t-secondary" title={String(currentRun.startedAt)}>
                      {formatRelative(currentRun.startedAt)}
                    </span>
                    {currentRun.label ? (
                      <span
                        className="badge badge--neutral"
                        title={`Run label: ${currentRun.label}`}
                        style={{
                          marginLeft: 'auto',
                          maxWidth: 320,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {currentRun.label}
                      </span>
                    ) : null}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      gap: 'var(--sp-5, 24px)',
                      flexWrap: 'wrap',
                      alignItems: 'end',
                    }}
                  >
                    <MiniStat label="Score">
                      {score && score.scorable > 0 ? (
                        <>
                          <PassBar rate={score.passRate} />
                          <span
                            style={{
                              fontFamily: 'var(--font-mono)',
                              color: passRateColor(score.passRate),
                            }}
                          >
                            {score.passed}/{score.scorable}
                          </span>
                          {(() => {
                            const prev = benchmark.previousRun
                              ? scoreRun(benchmark.previousRun)
                              : null;
                            if (
                              !prev ||
                              prev.passRate === null ||
                              score.passRate === null ||
                              prev.passRate === score.passRate
                            ) {
                              return null;
                            }
                            const up = score.passRate > prev.passRate;
                            const pts = Math.round(Math.abs(score.passRate - prev.passRate) * 100);
                            return (
                              <span
                                className="t-xs"
                                style={{
                                  fontFamily: 'var(--font-mono)',
                                  color: up ? 'var(--status-success)' : 'var(--status-danger)',
                                }}
                                title="Change vs previous run"
                              >
                                {up ? '▲' : '▼'} {pts}pt{pts === 1 ? '' : 's'}
                              </span>
                            );
                          })()}
                        </>
                      ) : (
                        <span className="t-secondary">—</span>
                      )}
                    </MiniStat>
                    {score && score.costUsd !== null ? (
                      <MiniStat label="Cost">
                        <span style={{ fontFamily: 'var(--font-mono)' }}>
                          {formatRunCost(score.costUsd)}
                        </span>
                      </MiniStat>
                    ) : null}
                    <MiniStat label="Duration">
                      <span className="t-secondary">
                        {formatDuration(currentRun.startedAt, currentRun.finishedAt)}
                      </span>
                    </MiniStat>
                    {runModelLabel(currentRun) ? (
                      <MiniStat label="Model">
                        <span className="t-secondary" title={runModelTitle(currentRun)}>
                          {runModelLabel(currentRun)}
                        </span>
                      </MiniStat>
                    ) : null}
                    {runAgentsUsed(currentRun) != null ? (
                      <MiniStat label="Agents">
                        <span className="t-secondary" title={runAgentsTitle(currentRun)}>
                          {runAgentsLabel(currentRun)}
                        </span>
                      </MiniStat>
                    ) : null}
                    {(currentRun.kitMetrics?.length ?? 0) > 0 ? (
                      <MiniStat label="Kit">
                        <span className="t-secondary">
                          {formatKitMetricsSummary(currentRun.kitMetrics)}
                        </span>
                      </MiniStat>
                    ) : null}
                    {currentRun.launchedThreads.length > 1 ? (
                      <MiniStat label="Threads">
                        <span
                          className="t-secondary"
                          title={currentRun.launchedThreads.map((thread) => thread.name).join(', ')}
                        >
                          {currentRun.launchedThreads.length}
                        </span>
                      </MiniStat>
                    ) : null}
                  </div>
                </div>
              )}
              {currentRun && currentRun.results.length > 0 && (
                <details>
                  <summary
                    className="t-sm t-secondary"
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    View latest results
                  </summary>
                  <div style={{ marginTop: 'var(--sp-2)' }}>
                    <BenchmarkResults
                      results={currentRun.results}
                      previous={benchmark.previousRun?.results}
                    />
                  </div>
                </details>
              )}
              <div style={{ ...row, flexWrap: 'wrap' }}>
                {!active && (
                  <button
                    className="btn btn--primary btn--sm"
                    disabled={start.isPending || !runnable}
                    title={
                      runnable
                        ? hasScenario
                          ? 'Start with driver scenario + kit'
                          : 'Start kit-only (no driver tool)'
                        : threadCount < 1
                          ? 'Add trial threads (Edit) before starting'
                          : 'Add a kit (watches/mission) or a driver Scenario ID'
                    }
                    onClick={() => {
                      setStartLabel('');
                      setStartTarget(benchmark);
                    }}
                  >
                    Start benchmark
                  </button>
                )}
                {currentRun?.status === 'RUNNING' && (
                  <button
                    className="btn btn--secondary btn--sm"
                    disabled={cancel.isPending}
                    onClick={() => cancel.mutate(benchmark.id)}
                  >
                    Cancel
                  </button>
                )}
                <Link
                  className="btn btn--secondary btn--sm"
                  to={routes.orchestrationBenchmark(benchmark.id)}
                >
                  Runs &amp; trends
                </Link>
                <button
                  className="btn btn--ghost btn--sm"
                  disabled={active}
                  onClick={() => {
                    setEditing(benchmark);
                    setShowForm(true);
                  }}
                >
                  Edit
                </button>
                <button
                  className="btn btn--ghost btn--sm"
                  style={{ marginLeft: 'auto' }}
                  disabled={active || remove.isPending}
                  onClick={() => setDeleteTarget(benchmark)}
                >
                  Delete
                </button>
              </div>
              {currentRun?.errorMessage && currentRun.status !== 'FAILED' && (
                <div className="alert alert--danger">{currentRun.errorMessage}</div>
              )}
            </div>
          </section>
        );
      })}

      <ConfirmDialog
        open={startTarget !== null}
        onClose={() => {
          if (!start.isPending) {
            setStartTarget(null);
            resetStartForm();
          }
        }}
        onConfirm={() => {
          if (startTarget && startMemoryMode) {
            start.mutate({ id: startTarget.id, body: buildStartBody() });
          }
        }}
        title="Start benchmark"
        message={
          <span style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
            {start.error ? (
              <div className="alert alert--danger" style={{ margin: 0 }}>
                {(start.error as Error).message}
              </div>
            ) : null}
            <span>
              Starting this benchmark will stop and clear all existing threads in “
              {startTarget
                ? (groupNames.get(startTarget.orchestrationGroupId) ?? 'Unknown group')
                : ''}
              ”. Threads in other orchestration groups are unaffected.
            </span>
            <label className="field" style={{ margin: 0 }}>
              <span className="field-label">Run label (optional)</span>
              <input
                className="input"
                value={startLabel}
                onChange={(e) => setStartLabel(e.target.value)}
                placeholder='e.g. "memory OFF", "grok-4.5", "baseline"'
                maxLength={120}
                disabled={start.isPending}
                autoFocus={true}
              />
              <span className="field-help">
                Tags this attempt in run history so you can tell trials apart later. Editable after
                the run finishes.
              </span>
            </label>

            <div style={{ ...row, gap: 'var(--sp-3)' }}>
              <label className="field" style={{ margin: 0, flex: 2 }}>
                <span className="field-label">Arm id (optional)</span>
                <input
                  className="input"
                  value={startArmId}
                  onChange={(e) => setStartArmId(e.target.value)}
                  placeholder='e.g. "mem-off", "grok"'
                  maxLength={120}
                  disabled={start.isPending}
                />
                <span className="field-help">
                  Groups trials of one experiment condition. Drift from an arm’s first run is
                  flagged automatically.
                </span>
              </label>
              <label className="field" style={{ margin: 0, flex: 1 }}>
                <span className="field-label">Trials</span>
                <input
                  className="input"
                  type="number"
                  min={1}
                  max={20}
                  value={startTrials}
                  onChange={(e) => setStartTrials(e.target.value)}
                  disabled={start.isPending}
                />
                <span className="field-help">Repeats (1–20) for reliability.</span>
              </label>
            </div>

            <span className="field-label" style={{ marginTop: 'var(--sp-2)' }}>
              Profile overrides — change ONE lever vs the group baseline
            </span>

            <label className="field" style={{ margin: 0 }}>
              <span className="field-label">Model</span>
              <select
                className="input"
                value={startModelConfigId}
                onChange={(e) => setStartModelConfigId(e.target.value)}
                disabled={start.isPending}
              >
                <option value="">Baseline (no override)</option>
                {(pageModelConfigs.data?.items ?? []).map((config) => (
                  <option key={config.id} value={config.id}>
                    {config.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="field" style={{ margin: 0 }}>
              <span className="field-label">Memory (required)</span>
              <select
                className="input"
                value={startMemoryMode}
                onChange={(e) => setStartMemoryMode(e.target.value)}
                disabled={start.isPending}
              >
                <option value="" disabled={true}>
                  Choose a memory mode…
                </option>
                <option value="OFF">OFF — ablated (no read, no writes)</option>
                <option value="LIVE">LIVE — isolated benchmark namespace</option>
                <option value="SEEDED" disabled={true}>
                  SEEDED — needs a pinned fixture (attach via API)
                </option>
              </select>
              <span className="field-help">
                A benchmark never touches the production store: LIVE runs in an isolated namespace
                (fresh per Start unless you continue one below); OFF is the memory-independent
                control.
              </span>
            </label>

            {startMemoryMode === 'LIVE' ? (
              <label className="field" style={{ margin: 0 }}>
                <span className="field-label">Continue namespace (optional)</span>
                <input
                  className="input"
                  value={startMemoryNamespaceId}
                  onChange={(e) => setStartMemoryNamespaceId(e.target.value)}
                  placeholder="namespace id from a previous LIVE run — empty = fresh namespace"
                  maxLength={120}
                  disabled={start.isPending}
                />
                <span className="field-help">
                  Paste a previous LIVE run&apos;s namespace id (on its run detail) to let this
                  Start build on the memories that run formed — the learning-curve arm.
                </span>
              </label>
            ) : null}

            <label className="field" style={{ margin: 0 }}>
              <span className="field-label">Tool refs (optional)</span>
              <input
                className="input"
                value={startToolRefs}
                onChange={(e) => setStartToolRefs(e.target.value)}
                placeholder="comma or newline separated — replaces the definition's toolRefs"
                disabled={start.isPending}
              />
              <span className="field-help">
                Tool refs replace the group&apos;s positive list. Disabled tools (below) subtract
                from whatever is effective after that.
              </span>
            </label>

            {startTarget ? (
              <DisabledToolsSelect
                benchmarkId={startTarget.id}
                value={startDisabledTools}
                onChange={setStartDisabledTools}
                disabled={start.isPending}
              />
            ) : null}

            <label className="field" style={{ margin: 0 }}>
              <span className="field-label">Instructions / charter (optional)</span>
              <textarea
                className="textarea"
                value={startInstructions}
                onChange={(e) => setStartInstructions(e.target.value)}
                placeholder="Replaces the group's system prompt for every launched thread"
                rows={3}
                disabled={start.isPending}
              />
            </label>
          </span>
        }
        confirmLabel={start.isPending ? 'Starting…' : 'Start benchmark'}
        pending={start.isPending}
        confirmDisabled={!startMemoryMode}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        onClose={() => {
          if (!remove.isPending) setDeleteTarget(null);
        }}
        onConfirm={() => {
          if (deleteTarget) remove.mutate(deleteTarget.id);
        }}
        title="Delete benchmark"
        message={`Delete benchmark “${deleteTarget?.name ?? ''}”?`}
        confirmLabel={remove.isPending ? 'Deleting…' : 'Delete'}
        destructive={true}
        pending={remove.isPending}
      />
    </div>
  );
}

function BenchmarkForm({
  benchmark,
  groups,
  connections,
  onCancel,
  onSaved,
}: {
  benchmark: Benchmark | null;
  groups: OrchestrationGroup[];
  connections: MCPConnection[];
  onCancel: () => void;
  onSaved: () => void;
}) {
  const client = useClient();
  const [name, setName] = useState(benchmark?.name ?? '');
  const [description, setDescription] = useState(benchmark?.description ?? '');
  const [groupId, setGroupId] = useState(benchmark?.orchestrationGroupId ?? groups[0]?.id ?? '');
  const [mcpId, setMcpId] = useState(benchmark?.mcpConnectionId ?? connections[0]?.id ?? '');
  const [remoteKey, setRemoteKey] = useState(benchmark?.remoteBenchmarkKey ?? '');
  const [driverKind, setDriverKind] = useState<BenchmarkDriverKind>(() =>
    benchmark?.driver?.kind === 'sim_scenario' ? 'sim_scenario' : 'benchmark_tool',
  );
  const [httpBaseUrl, setHttpBaseUrl] = useState(
    () => benchmark?.driver?.httpBaseUrl?.trim() ?? '',
  );
  const [projection, setProjection] = useState<BenchmarkScenarioProjection>(() =>
    benchmark?.driver?.projection === 'none' ? 'none' : 'sim_events',
  );
  const [scorecardAuth, setScorecardAuth] = useState<BenchmarkScorecardAuth>(() =>
    benchmark?.driver?.scorecardAuth === 'scorer' ? 'scorer' : 'connection',
  );
  const [showDriverAdvanced, setShowDriverAdvanced] = useState(false);
  const [toolList, setToolList] = useState(() => benchmark?.driver?.tools?.list ?? '');
  const [toolBriefing, setToolBriefing] = useState(() => benchmark?.driver?.tools?.briefing ?? '');
  const [toolStart, setToolStart] = useState(() => benchmark?.driver?.tools?.start ?? '');
  const [toolStatus, setToolStatus] = useState(() => benchmark?.driver?.tools?.status ?? '');
  const [toolAbort, setToolAbort] = useState(() => benchmark?.driver?.tools?.abort ?? '');
  const [scorecardPath, setScorecardPath] = useState(
    () => benchmark?.driver?.scorecardPathTemplate ?? '',
  );
  const [timeoutSeconds, setTimeoutSeconds] = useState(benchmark?.timeoutSeconds ?? 1200);
  const [pollIntervalSeconds, setPollIntervalSeconds] = useState(
    benchmark?.pollIntervalSeconds ?? 5,
  );
  const [judgeModelConfigId, setJudgeModelConfigId] = useState(benchmark?.judgeModelConfigId ?? '');
  const [threads, setThreads] = useState<IThreadDraft[]>(() =>
    (benchmark?.threadStartupConfig.agents ?? []).flatMap((agent) =>
      agent.threads.map((thread) => ({
        id: crypto.randomUUID(),
        agentDefinitionId: agent.agentDefinitionId,
        name: thread.name,
        facts: Object.fromEntries(
          thread.initialFacts.map((fact) => [fact.laneId, fact.summary ?? String(fact.data ?? '')]),
        ),
        initialNotification: thread.initialNotification ?? '',
        ...(thread.anchors ? { anchors: thread.anchors } : {}),
      })),
    ),
  );
  const [kit, setKit] = useState<BenchmarkKit>(() =>
    benchmark ? parseBenchmarkKit(benchmark.kit) : emptyBenchmarkKit(),
  );

  const definitions = useQuery({
    queryKey: ['persistent-agent-groups', groupId],
    queryFn: () =>
      client.http
        .get('v1/persistent-agent-groups', { searchParams: { limit: '200' } })
        .json<IListEnvelope<PersistentAgentGroup>>(),
    enabled: Boolean(groupId),
  });
  const groupDefinitions = (definitions.data?.items ?? []).filter(
    (definition) => definition.orchestrationGroupId === groupId,
  );
  const modelConfigs = useQuery({
    queryKey: ['model-configs'],
    queryFn: () => client.http.get('v1/model-configs?limit=200').json<IModelListEnvelope>(),
    staleTime: 60_000,
  });
  const catalog = useQuery({
    queryKey: ['benchmark-catalog', mcpId],
    queryFn: () =>
      client.http
        .get('v1/benchmarks/catalog', { searchParams: { mcpConnectionId: mcpId } })
        .json<BenchmarkCatalogItem[]>(),
    enabled: Boolean(mcpId),
    retry: false,
  });

  // Only asked when it matters: a sim benchmark that fetches a scorecard with
  // the Scorer token. Status only, never the token itself.
  const scorerStatus = useScorerCredentialStatus(
    client,
    mcpId,
    driverKind === 'sim_scenario' && projection === 'sim_events' && scorecardAuth === 'scorer',
  );
  const mcpName = connections.find((c) => c.id === mcpId)?.name ?? 'this connection';

  const save = useMutation({
    mutationFn: () => {
      const agents = buildThreadStartupAgents(threads, groupDefinitions);
      const body: Record<string, unknown> = {
        name: name.trim(),
        description: description.trim() || undefined,
        orchestrationGroupId: groupId,
        mcpConnectionId: mcpId,
        remoteBenchmarkKey: remoteKey.trim(),
        timeoutSeconds,
        pollIntervalSeconds,
        judgeModelConfigId: judgeModelConfigPayload(judgeModelConfigId),
        threadStartupConfig: { agents },
        kit: {
          watches: kit.watches.filter((w) => w.name.trim() && w.toolName.trim() && w.laneId.trim()),
          scorecard: kit.scorecard ?? null,
        },
      };
      const driver = buildBenchmarkDriverPayload(
        driverKind,
        {
          httpBaseUrl,
          projection,
          scorecardAuth,
          tools: {
            list: toolList,
            briefing: toolBriefing,
            start: toolStart,
            status: toolStatus,
            abort: toolAbort,
          },
          scorecardPathTemplate: scorecardPath,
        },
        benchmark?.driver ?? null,
      );
      if (driver) body.driver = driver;
      return benchmark
        ? client.http.patch(`v1/benchmarks/${benchmark.id}`, { json: body }).json<Benchmark>()
        : client.http.post('v1/benchmarks', { json: body }).json<Benchmark>();
    },
    onSuccess: onSaved,
  });

  const definitionsById = useMemo(
    () => new Map(groupDefinitions.map((definition) => [definition.id, definition])),
    [groupDefinitions],
  );
  /** Draft save: name + group + MCP. Scenario id + threads required only to Start. */
  const canSave =
    Boolean(name.trim() && groupId && mcpId) &&
    threads.every((thread) => thread.name.trim() && definitionsById.has(thread.agentDefinitionId));

  return (
    <section className="card">
      <div className="card-header">
        <h3 className="t-h3">{benchmark ? 'Edit benchmark' : 'New benchmark'}</h3>
      </div>
      <div className="card-body" style={stack3}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-3)' }}>
          <label className="field">
            <span className="field-label">Name</span>
            <input
              className="input"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </label>
          <label className="field">
            <span className="field-label">Orchestration group</span>
            <select
              className="select-native"
              value={groupId}
              onChange={(event) => {
                setGroupId(event.target.value);
                setThreads([]);
              }}
            >
              <option value="">Select a group…</option>
              {groups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                  {group.isFavorite ? ' ★' : ''}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field-label">Benchmark-driving MCP</span>
            <select
              className="select-native"
              value={mcpId}
              onChange={(event) => {
                setMcpId(event.target.value);
                setRemoteKey('');
              }}
            >
              <option value="">Select an MCP…</option>
              {connections.map((connection) => (
                <option key={connection.id} value={connection.id}>
                  {connection.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field-label">Driver</span>
            <select
              className="select-native"
              value={driverKind}
              onChange={(event) => setDriverKind(event.target.value as BenchmarkDriverKind)}
            >
              <option value="benchmark_tool">Benchmark MCP tool</option>
              <option value="sim_scenario">Simulator scenario (external sim)</option>
            </select>
            <span className="field-help">
              {driverKind === 'sim_scenario'
                ? 'Starts a scripted external sim scenario, polls to completion, and scores via the sim HTTP scorecard.'
                : 'Uses the MCP connection’s benchmark tool contract (legacy).'}
            </span>
          </label>
          <label className="field">
            <span className="field-label">
              {driverKind === 'sim_scenario'
                ? 'Scenario name (for Start)'
                : 'Scenario ID (for Start)'}
            </span>
            <select
              className="select-native"
              value={remoteKey}
              onChange={(event) => setRemoteKey(event.target.value)}
            >
              <option value="">
                {catalog.isLoading
                  ? 'Loading…'
                  : driverKind === 'sim_scenario'
                    ? 'Optional until Start — pick a scenario (e.g. easy)…'
                    : 'Optional until Start — pick a driver scenario…'}
              </option>
              {(catalog.data ?? []).map((item) => (
                <option key={item.key} value={item.key}>
                  {item.name ?? item.key}
                </option>
              ))}
              {remoteKey && !(catalog.data ?? []).some((item) => item.key === remoteKey) && (
                <option value={remoteKey}>{remoteKey}</option>
              )}
            </select>
            {catalog.error && (
              <span className="field-help" style={{ color: 'var(--status-danger)' }}>
                {driverKind === 'sim_scenario'
                  ? 'Could not load scenarios from the sim (list_scenarios) — you can still save a draft and set the scenario later.'
                  : 'The MCP’s benchmark catalog could not be loaded — you can still save a draft and set the scenario later.'}
              </span>
            )}
            {!remoteKey.trim() ? (
              <span className="field-help">
                {driverKind === 'sim_scenario' ? (
                  <>
                    Optional until Start. Catalog comes from the sim’s <code>list_scenarios</code>{' '}
                    (names like <code>easy</code>).
                  </>
                ) : (
                  <>
                    Optional. Leave empty for <strong>kit-only</strong> mode (any MCP tools via
                    watches + scorecard — no <code>benchmark</code> driver tool needed).
                  </>
                )}
              </span>
            ) : (
              <span className="field-help">
                {driverKind === 'sim_scenario' ? (
                  <>
                    Flow: list → <code>get_scenario_briefing</code> → <code>start_scenario</code>.
                    Briefing is injected into the trial wake when present.
                  </>
                ) : (
                  <>
                    Driver scenario key for MCP tool <code>benchmark</code> (sim reset / score).
                    Omit for kit-only.
                  </>
                )}
              </span>
            )}
          </label>
          {driverKind === 'sim_scenario' ? (
            <>
              <label className="field" style={{ gridColumn: '1 / -1' }}>
                <span className="field-label">Scoring mode</span>
                <select
                  className="select-native"
                  value={projection}
                  onChange={(event) =>
                    setProjection(event.target.value as BenchmarkScenarioProjection)
                  }
                >
                  <option value="sim_events">External event scorecard (drone-sim shape)</option>
                  <option value="none">Kit + LLM cost only (no external scorecard)</option>
                </select>
                <span className="field-help">
                  Use <strong>kit + cost only</strong> for cheap local / stub MCP benches — the
                  driver still starts/polls/aborts via MCP tools, but does not fetch a ground-truth
                  HTTP scorecard.
                </span>
              </label>
              {projection === 'sim_events' ? (
                <label className="field" style={{ gridColumn: '1 / -1' }}>
                  <span className="field-label">
                    Scorecard HTTP base URL (optional — derived from the MCP endpoint)
                  </span>
                  <input
                    className="input"
                    value={httpBaseUrl}
                    placeholder="https://host/world (leave blank to derive from MCP endpoint)"
                    onChange={(event) => setHttpBaseUrl(event.target.value)}
                  />
                  <span className="field-help">
                    Used for{' '}
                    <code>
                      GET {'{base}'}
                      {scorecardPath.trim() || '/api/scenarios/runs/{runId}'}
                    </code>
                    . When empty, trailing <code>/mcp</code> or <code>/sse</code> is stripped from
                    the MCP connection endpoint.
                  </span>
                </label>
              ) : null}
              {projection === 'sim_events' ? (
                <label className="field" style={{ gridColumn: '1 / -1' }}>
                  <span className="field-label">Scorecard credential</span>
                  <select
                    className="select-native"
                    value={scorecardAuth}
                    onChange={(event) =>
                      setScorecardAuth(event.target.value as BenchmarkScorecardAuth)
                    }
                  >
                    <option value="connection">Connection credential (default)</option>
                    <option value="scorer">Scorer token (sims that hide the answer key)</option>
                  </select>
                  <span className="field-help">
                    Which credential fetches finished run records for scoring and replay.{' '}
                    <strong>Connection credential</strong> reuses the MCP connection’s own token
                    (one-token sims such as formant-sims). <strong>Scorer token</strong> uses only
                    the connection’s Scorer token, never the agent’s; Start checks it against the
                    sim first and refuses to run if it is missing or rejected.{' '}
                    <a href={simBenchmarkSetupDocUrl} target="_blank" rel="noreferrer">
                      Setup guide
                    </a>
                  </span>
                  {scorecardAuth === 'scorer' &&
                  mcpId &&
                  scorerStatus.data?.configured === false ? (
                    <div
                      className="alert alert--warning"
                      role="alert"
                      style={{ marginTop: 'var(--sp-2)' }}
                    >
                      MCP connection “{mcpName}” has no Scorer token, so Start will refuse to run.
                      Set it on the MCP connections page (Edit credentials, then Scorer token).
                    </div>
                  ) : null}
                </label>
              ) : null}
              <div style={{ gridColumn: '1 / -1' }}>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => setShowDriverAdvanced((v) => !v)}
                >
                  {showDriverAdvanced ? 'Hide' : 'Show'} advanced MCP tool mapping
                </button>
                {showDriverAdvanced ? (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 'var(--sp-3)',
                      marginTop: 'var(--sp-2)',
                    }}
                  >
                    {(
                      [
                        ['List tool', toolList, setToolList, 'list_scenarios'],
                        ['Briefing tool', toolBriefing, setToolBriefing, 'get_scenario_briefing'],
                        ['Start tool', toolStart, setToolStart, 'start_scenario'],
                        ['Status tool', toolStatus, setToolStatus, 'get_scenario_status'],
                        ['Abort tool', toolAbort, setToolAbort, 'abort_scenario'],
                      ] as const
                    ).map(([label, value, setValue, placeholder]) => (
                      <label key={label} className="field">
                        <span className="field-label">{label}</span>
                        <input
                          className="input"
                          value={value}
                          placeholder={placeholder}
                          onChange={(e) => setValue(e.target.value)}
                        />
                      </label>
                    ))}
                    {projection === 'sim_events' ? (
                      <label className="field" style={{ gridColumn: '1 / -1' }}>
                        <span className="field-label">Scorecard path template</span>
                        <input
                          className="input"
                          value={scorecardPath}
                          placeholder="/api/scenarios/runs/{runId}"
                          onChange={(e) => setScorecardPath(e.target.value)}
                        />
                        <span className="field-help">
                          Relative to the HTTP base. <code>{'{runId}'}</code> is replaced with the
                          remote run id from the start tool.
                        </span>
                      </label>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </>
          ) : null}
        </div>
        <label className="field">
          <span className="field-label">Description</span>
          <textarea
            className="textarea"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </label>

        <div>
          <h4 className="t-h4">Trial threads (required to Start)</h4>
          <p className="t-sm t-secondary">
            On Start, every existing thread in this orchestration group is deleted, then these named
            threads are created fresh and the kit is installed on each. Draft kits can omit this;
            runnable benches cannot.
          </p>
          {definitions.isLoading && <Spinner inline={true} />}
          {!definitions.isLoading && groupId && groupDefinitions.length === 0 && (
            <p className="t-sm" style={{ color: 'var(--status-danger)' }}>
              No agent definitions in this orchestration group. Create/publish a PA definition in
              the group first, then come back and Add thread.
            </p>
          )}
          {groupDefinitions.map((definition) => (
            <div key={definition.id} className="card" style={{ marginTop: 8, padding: 12 }}>
              <div style={{ ...row, justifyContent: 'space-between' }}>
                <strong>{definition.name}</strong>
                <button
                  className="btn btn--secondary btn--sm"
                  onClick={() =>
                    setThreads((current) => [
                      ...current,
                      {
                        id: crypto.randomUUID(),
                        agentDefinitionId: definition.id,
                        name: '',
                        facts: {},
                        initialNotification: '',
                      },
                    ])
                  }
                >
                  Add thread
                </button>
              </div>
              {threads
                .filter((thread) => thread.agentDefinitionId === definition.id)
                .map((thread) => (
                  <div key={thread.id} style={{ ...stack3, marginTop: 12 }}>
                    <div style={row}>
                      <input
                        className="input"
                        placeholder="Thread name"
                        value={thread.name}
                        onChange={(event) =>
                          setThreads((current) =>
                            current.map((item) =>
                              item.id === thread.id ? { ...item, name: event.target.value } : item,
                            ),
                          )
                        }
                      />
                      <button
                        className="btn btn--ghost btn--sm"
                        onClick={() =>
                          setThreads((current) => current.filter((item) => item.id !== thread.id))
                        }
                      >
                        Remove
                      </button>
                    </div>
                    {definition.requiredFacts.map((fact) => (
                      <label className="field" key={fact.laneId}>
                        <span className="field-label">Required fact: {fact.laneId}</span>
                        <input
                          className="input"
                          value={thread.facts[fact.laneId] ?? ''}
                          onChange={(event) =>
                            setThreads((current) =>
                              current.map((item) =>
                                item.id === thread.id
                                  ? {
                                      ...item,
                                      facts: { ...item.facts, [fact.laneId]: event.target.value },
                                    }
                                  : item,
                              ),
                            )
                          }
                        />
                      </label>
                    ))}
                    <label className="field">
                      <span className="field-label">Initial notification (optional)</span>
                      <textarea
                        className="textarea"
                        placeholder="A per-thread notification delivered when the benchmark starts"
                        value={thread.initialNotification}
                        onChange={(event) =>
                          setThreads((current) =>
                            current.map((item) =>
                              item.id === thread.id
                                ? { ...item, initialNotification: event.target.value }
                                : item,
                            ),
                          )
                        }
                      />
                    </label>
                  </div>
                ))}
            </div>
          ))}
        </div>

        <BenchmarkKitEditor
          value={kit}
          onChange={setKit}
          mcpConnectionId={mcpId}
          groupId={groupId}
          connections={connections.map((c) => ({ id: c.id, name: c.name }))}
        />

        <div style={row}>
          <label className="field">
            <span className="field-label">Timeout (seconds)</span>
            <input
              className="input"
              type="number"
              min={10}
              value={timeoutSeconds}
              onChange={(event) => setTimeoutSeconds(Number(event.target.value))}
            />
          </label>
          <label className="field">
            <span className="field-label">Poll interval (seconds)</span>
            <input
              className="input"
              type="number"
              min={1}
              value={pollIntervalSeconds}
              onChange={(event) => setPollIntervalSeconds(Number(event.target.value))}
            />
          </label>
        </div>
        <label className="field">
          <span className="field-label">Strategy judge (optional)</span>
          <select
            className="select-native"
            value={judgeModelConfigId}
            onChange={(event) => setJudgeModelConfigId(event.target.value)}
            aria-label="Strategy judge model config"
          >
            <option value="">No strategy judging</option>
            {(modelConfigs.data?.items ?? []).map((config) => (
              <option key={config.id} value={config.id}>
                {config.name}
              </option>
            ))}
            {judgeModelConfigId &&
              !(modelConfigs.data?.items ?? []).some((c) => c.id === judgeModelConfigId) && (
                <option value={judgeModelConfigId}>{judgeModelConfigId}</option>
              )}
          </select>
          <span className="field-help">
            After each completed run, a stack-blind LLM judge writes a doctrine analysis (the
            &ldquo;shift report&rdquo;) with this model. Judged results add <code>strategy_*</code>{' '}
            rows and cost one model call per run.
          </span>
        </label>
        {save.error && <div className="alert alert--danger">{(save.error as Error).message}</div>}
        <div style={row}>
          <button
            className="btn btn--primary btn--sm"
            disabled={!canSave || save.isPending}
            onClick={() => save.mutate()}
            title={
              !canSave
                ? 'Name, orchestration group, and MCP are required'
                : !remoteKey.trim() || threads.length === 0
                  ? 'Saved as a draft — add scenario ID + trial threads before Start'
                  : undefined
            }
          >
            {save.isPending
              ? 'Saving…'
              : !remoteKey.trim() || threads.length === 0
                ? 'Save draft'
                : 'Save benchmark'}
          </button>
          <button className="btn btn--ghost btn--sm" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </section>
  );
}

export type ScenarioDriverFormExtras = {
  httpBaseUrl: string;
  projection: BenchmarkScenarioProjection;
  /** Omitted or `connection` = the default, not written to the payload. */
  scorecardAuth?: BenchmarkScorecardAuth;
  tools: { list: string; briefing: string; start: string; status: string; abort: string };
  scorecardPathTemplate: string;
};

/**
 * Build the optional `driver` field for create/update.
 * Omits entirely for legacy-clean benchmark_tool rows with no overrides.
 */
interface IModelListEnvelope {
  items: ModelConfig[];
  total: number;
}

/** Judge opt-in payload value: a config id, or null = "No strategy judging". */
export function judgeModelConfigPayload(value: string): string | null {
  const trimmed = value.trim();
  return trimmed || null;
}

/** The experiment-arm Start form's fields (DEV-17197). `disabledTools` is already-canonical ids, not a raw string. */
export interface IStartBenchmarkFields {
  label: string;
  armId: string;
  trials: string;
  modelConfigId: string;
  instructions: string;
  toolRefs: string;
  disabledTools: string[];
  memoryMode: string;
  /** Namespace to continue (DEV-17254); only sent with memoryMode 'LIVE'. */
  memoryNamespaceId?: string;
}

/**
 * Build a StartBenchmarkInput body from the arm form — omitting every empty
 * field so a baseline arm posts `{}` (never a profile of nulls), and nesting
 * the one-lever overrides under `profile` only when at least one is set. The
 * `trials` field is only sent when it parses to a valid 1–20 integer.
 * `disabledTools` is the NEGATIVE tools-axis lever (DEV-17197): posted
 * verbatim when non-empty, and composed with `toolRefs` in the same profile.
 */
export function buildStartBenchmarkBody(fields: IStartBenchmarkFields): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (fields.label.trim()) body.label = fields.label.trim();
  if (fields.armId.trim()) body.armId = fields.armId.trim();
  const trials = Number(fields.trials);
  if (Number.isInteger(trials) && trials >= 1 && trials <= 20) body.trials = trials;
  const profile: Record<string, unknown> = {};
  if (fields.modelConfigId) profile.modelConfigId = fields.modelConfigId;
  if (fields.instructions.trim()) profile.instructions = fields.instructions.trim();
  const refs = fields.toolRefs
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (refs.length > 0) profile.toolRefs = refs;
  if (fields.disabledTools.length > 0) profile.disabledTools = fields.disabledTools;
  if (fields.memoryMode) profile.memoryMode = fields.memoryMode;
  if (Object.keys(profile).length > 0) body.profile = profile;
  // Namespace continuation rides the Start body, not the profile — the
  // service mints/validates and injects it into the profile itself
  // (DEV-17254). Only meaningful for LIVE arms.
  if (fields.memoryMode === 'LIVE' && fields.memoryNamespaceId?.trim()) {
    body.memoryNamespaceId = fields.memoryNamespaceId.trim();
  }
  return body;
}

export function buildBenchmarkDriverPayload(
  kind: BenchmarkDriverKind,
  extras: ScenarioDriverFormExtras | string = {
    httpBaseUrl: '',
    projection: 'sim_events',
    tools: { list: '', briefing: '', start: '', status: '', abort: '' },
    scorecardPathTemplate: '',
  },
  /**
   * The benchmark's CURRENT driver config, so keys this form does not edit
   * (startScenarioArg, startSeedArg, startRunIdField, completeStatuses,
   * failedStatuses, fetchBriefing, classicalDispatch) survive a save instead
   * of being wiped by the wholesale driver PATCH. Carried only when the kind
   * is unchanged — switching driver kinds starts clean.
   */
  existing?: BenchmarkDriverConfig | null,
): BenchmarkDriverConfig | undefined {
  const carried =
    existing && existing.kind === kind
      ? {
          ...(existing.startScenarioArg ? { startScenarioArg: existing.startScenarioArg } : {}),
          ...(existing.startSeedArg ? { startSeedArg: existing.startSeedArg } : {}),
          ...(existing.startRunIdField ? { startRunIdField: existing.startRunIdField } : {}),
          ...(existing.completeStatuses ? { completeStatuses: existing.completeStatuses } : {}),
          ...(existing.failedStatuses ? { failedStatuses: existing.failedStatuses } : {}),
          ...(existing.fetchBriefing !== undefined
            ? { fetchBriefing: existing.fetchBriefing }
            : {}),
          ...(existing.classicalDispatch ? { classicalDispatch: existing.classicalDispatch } : {}),
        }
      : {};
  // Back-compat: older tests passed a bare httpBaseUrl string.
  const opts: ScenarioDriverFormExtras =
    typeof extras === 'string'
      ? {
          httpBaseUrl: extras,
          projection: 'sim_events',
          tools: { list: '', briefing: '', start: '', status: '', abort: '' },
          scorecardPathTemplate: '',
        }
      : extras;

  if (kind === 'benchmark_tool') {
    const trimmed = opts.httpBaseUrl.trim();
    if (!trimmed) return undefined;
    return { kind, ...carried, httpBaseUrl: trimmed };
  }

  const toolsEntries = Object.entries(opts.tools)
    .map(([k, v]) => [k, v.trim()] as const)
    .filter(([, v]) => v.length > 0);
  const tools =
    toolsEntries.length > 0
      ? (Object.fromEntries(toolsEntries) as BenchmarkDriverConfig['tools'])
      : undefined;
  const path = opts.scorecardPathTemplate.trim();
  const http = opts.httpBaseUrl.trim();

  return {
    kind: 'sim_scenario',
    ...carried,
    projection: opts.projection,
    ...(http ? { httpBaseUrl: http } : {}),
    ...(tools ? { tools } : {}),
    ...(path ? { scorecardPathTemplate: path } : {}),
    // Only the non-default is written; `connection` stays implicit so
    // existing benchmarks save byte-for-byte as before.
    ...(opts.scorecardAuth === 'scorer' && opts.projection !== 'none'
      ? { scorecardAuth: 'scorer' as const }
      : {}),
  };
}

export function buildThreadStartupAgents(
  threads: Array<{
    agentDefinitionId: string;
    name: string;
    facts: Record<string, string>;
    initialNotification?: string;
    anchors?: MemoryAnchorSet;
  }>,
  definitions: Array<Pick<PersistentAgentGroup, 'id' | 'requiredFacts'>>,
) {
  return [...new Set(threads.map((thread) => thread.agentDefinitionId))].map(
    (agentDefinitionId) => {
      const definition = definitions.find((item) => item.id === agentDefinitionId);
      return {
        agentDefinitionId,
        threads: threads
          .filter((thread) => thread.agentDefinitionId === agentDefinitionId)
          .map((thread) => ({
            name: thread.name.trim(),
            initialFacts: (definition?.requiredFacts ?? []).map((fact) => ({
              laneId: fact.laneId,
              type: fact.type,
              data: thread.facts[fact.laneId] ?? '',
              summary: thread.facts[fact.laneId] ?? '',
            })),
            ...(thread.initialNotification?.trim()
              ? { initialNotification: thread.initialNotification.trim() }
              : {}),
            ...(thread.anchors && Object.keys(thread.anchors).length > 0
              ? { anchors: thread.anchors }
              : {}),
          })),
      };
    },
  );
}
